import Decimal from 'decimal.js';
import { Stowage } from 'src/types/ViewModel/Stowage/Stowage';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { isTransitOperation } from '../cargoOperationUtils';
import { calculateVolume } from './volumeUtils';
import { getCargoTemperature } from './cargoTemperatureUtils';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';

export const getFilteredStowageItems = (stowagePlan: StowagePlan, cargoOperation: RotationCargoOperation, excludeTankId?: number) => {
    const baseFilter = (s: any) => s.cargoOperationGuid === cargoOperation.cargoOperationGuid;
    const tankFilter = excludeTankId ? (s: any) => s.tankId !== excludeTankId : () => true;
    const transferFilter = (s: any) => cargoOperation.operationType !== 'Transfer' || s.weight.greaterThan(0);

    const filter = (s: any) => baseFilter(s) && tankFilter(s) && transferFilter(s);

    return (isTransitOperation(cargoOperation) ? stowagePlan.transitStowageItems : stowagePlan.stowageItems).filter(filter);
};

export const stowedWeightForCargoOperationInOtherTanks = (stowagePlan: StowagePlan, tank: Tank, cargoOperation: RotationCargoOperation): Decimal => {
    const filteredItems = getFilteredStowageItems(stowagePlan, cargoOperation, tank.tankId);
    return filteredItems.reduce((acc, item) => acc.plus(item.weight), new Decimal(0));
};

export const stowedWeightInTanks = (stowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): Decimal => {
    const filteredItems = getFilteredStowageItems(stowagePlan, cargoOperation);
    return filteredItems.reduce((acc, item) => acc.plus(item.weight), new Decimal(0));
};

export const notStowedWeightInTanks = (stowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): Decimal => {
    const nominatedWeight = new Decimal(cargoOperation.nominatedWeight ?? 0);
    return (cargoOperation.operationType === 'Loading' || cargoOperation.operationType === 'Transit' ? nominatedWeight : nominatedWeight.neg()).minus(
        stowedWeightInTanks(stowagePlan, cargoOperation)
    );
};

export const notStowedVolume = (stowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): Decimal => {
    const _notStowedWeight = notStowedWeightInTanks(stowagePlan, cargoOperation);
    return calculateVolume(_notStowedWeight, cargoOperation.cargo, getCargoTemperature(stowagePlan, cargoOperation.cargo));
};

export const stowedVolume = (stowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): Decimal => {
    const _stowedWeight = stowedWeightInTanks(stowagePlan, cargoOperation);
    return calculateVolume(_stowedWeight, cargoOperation.cargo, getCargoTemperature(stowagePlan, cargoOperation.cargo));
};

export const calculateFillRatio = (volume: Decimal, tank: Tank): Decimal => {
    return volume.dividedBy(new Decimal(tank.volume));
};

export const getTanksWeightCapacity = (selectedTanks: Tank[]): number => {
    return selectedTanks.reduce((acc, tank) => acc + tank.maxWeightAt98Percent, 0);
};

export const getTanksVolume = (selectedTanks: Tank[]): number => {
    return selectedTanks.reduce((acc, tank) => acc + tank.volume, 0);
};

export const getStowage = (stowagePlan: StowagePlan, cargoOperation: RotationCargoOperation, tank: Tank): Stowage => {
    return stowagePlan.stowageItems.find((s) => s.cargoOperationGuid === cargoOperation.cargoOperationGuid && s.tankId === tank.tankId);
};

export const getShipFigure = (stowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): Decimal => {
    const stowageItems = getFilteredStowageItems(stowagePlan, cargoOperation);
    return stowageItems.reduce((acc, s) => acc.plus(s.isShipFigureAdjusted ? s.shipFigure : s.weight), new Decimal(0));
};

export const isShipFigureAdjusted = (stowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): boolean => {
    const stowageItems = getFilteredStowageItems(stowagePlan, cargoOperation);
    return stowageItems.some((s) => s.isShipFigureAdjusted);
};

export const getCargoOperationStowedTankIds = (stowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): number[] => {
    return getFilteredStowageItems(stowagePlan, cargoOperation).map((s) => s.tankId);
};

export const getCargoOperationStowedTankNames = (stowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): string[] => {
    return getFilteredStowageItems(stowagePlan, cargoOperation).map((s) => s.tank.odfjellTankName);
};

export const getCargoStowedTanks = (stowagePlan: StowagePlan, cargo: RotationCargo): Tank[] => {
    return stowagePlan.stowageItems.filter((s) => s.cargoOperation.cargoGuid === cargo.cargoGuid).map((s) => s.tank);
};

/**
 * Returns a deduplicated list of tanks where the cargo is stowed (loading or transfer-to).
 * Unlike getCargoStowedTanks, this only returns tanks with positive cargo weight
 * (loading or transfer-to) and ensures each tank appears at most once.
 * @param stowagePlan The stowage plan
 * @param cargo The cargo to find tanks for
 * @returns Deduplicated array of tanks containing the cargo
 */
export const getUniqueCargoStowedTanks = (stowagePlan: StowagePlan, cargo: RotationCargo): Tank[] => {
    const tanks = stowagePlan.stowageItems
        .filter(
            (s) =>
                s.cargoOperation.cargoGuid === cargo.cargoGuid &&
                (s.cargoOperation.operationType === 'Loading' ||
                    (s.cargoOperation.operationType === 'Transfer' && s.weight.gt(0)))
        )
        .map((s) => s.tank);

    // Deduplicate by tankId
    return [...new Map(tanks.map((t) => [t.tankId, t])).values()];
};
